import type { ImportBatchRow, ImportTarget, RosterResolutionAction, RowChoice } from '@discovr/types';

export type ReviewRowStatus =
  | 'needs-fix'
  | 'duplicate'
  | 'in-campaign'
  | 'will-update'
  | 'keeping-current'
  | 'no-changes'
  | 'ready';

/** Lower rank = more urgent; a row shows only its most urgent status. */
export const REVIEW_STATUS_RANK: Record<ReviewRowStatus, number> = {
  'needs-fix': 1,
  duplicate: 2,
  'in-campaign': 3,
  'will-update': 4,
  'keeping-current': 5,
  'no-changes': 6,
  ready: 7,
};

export const REVIEW_STATUS_LABEL: Record<ReviewRowStatus, string> = {
  'needs-fix': 'Needs fix',
  duplicate: 'Duplicate',
  'in-campaign': 'In campaign',
  'will-update': 'Will update',
  'keeping-current': 'Keeping current',
  'no-changes': 'No changes',
  ready: 'Ready',
};

// An outreach import skips every creator already in the campaign, so its
// "no changes" rows are creators it will not add again.
const OUTREACH_LABEL_OVERRIDES: Partial<Record<ReviewRowStatus, string>> = {
  'no-changes': 'Already added',
};

export function reviewStatusLabel(status: ReviewRowStatus, target: ImportTarget): string {
  if (target === 'outreach') return OUTREACH_LABEL_OVERRIDES[status] ?? REVIEW_STATUS_LABEL[status];
  return REVIEW_STATUS_LABEL[status];
}

const LAST_BLOCKING_RANK = 3;
const MIN_DUPLICATE_GROUP_SIZE = 2;

const IMPORTABLE_RESOLUTIONS: readonly RosterResolutionAction[] = ['add', 'overwrite'];

const STATUS_BY_RESOLUTION: Partial<Record<RosterResolutionAction, ReviewRowStatus>> = {
  overwrite: 'will-update',
  keep_existing: 'keeping-current',
  skip: 'no-changes',
};

const CHOICE_BY_RESOLUTION: Partial<Record<RosterResolutionAction, RowChoice>> = {
  overwrite: 'sheet',
  keep_existing: 'current',
  '': 'undecided',
};

function hasFieldErrors(row: ImportBatchRow): boolean {
  return Object.keys(row.validationErrors).length > 0;
}

export function isDuplicateRow(row: ImportBatchRow): boolean {
  return row.duplicateGroupSize >= MIN_DUPLICATE_GROUP_SIZE;
}

function isUndecidedMatch(row: ImportBatchRow): boolean {
  return row.rosterMatch !== null && row.rosterResolution === '';
}

/** Mirrors the backend's hasNoRosterLookupResult: every looked-up row ends
 *  with a match or "add", so neither means it was never looked up. It ranks
 *  as needs-fix (not in-campaign) because there is no match to decide on —
 *  re-entering the username is what triggers the lookup. */
function hasNoRosterLookupResult(row: ImportBatchRow): boolean {
  return row.rosterMatch === null && row.rosterResolution === '';
}

const DROPPED_SHEET_RESOLUTION: ImportBatchRow['sheetResolution'] = 'drop';

/** Deleted, exact-copy and skipped-at-import rows never reach the commit.
 *  A sheet-level drop counts too even without a reason — the backend always
 *  sets both, but this must not depend on it (same rule as features'
 *  importRowChanges). */
export function isDroppedRow(row: ImportBatchRow): boolean {
  return row.dropReason !== '' || row.sheetResolution === DROPPED_SHEET_RESOLUTION;
}

export function isBlockingStatus(status: ReviewRowStatus): boolean {
  return REVIEW_STATUS_RANK[status] <= LAST_BLOCKING_RANK;
}

export function reviewRowStatus(row: ImportBatchRow): ReviewRowStatus {
  if (hasFieldErrors(row)) return 'needs-fix';
  if (hasNoRosterLookupResult(row)) return 'needs-fix';
  if (isDuplicateRow(row)) return 'duplicate';
  if (isUndecidedMatch(row)) return 'in-campaign';
  if (row.rosterMatch === null) return 'ready';
  return STATUS_BY_RESOLUTION[row.rosterResolution] ?? 'ready';
}

export function rowChoice(row: ImportBatchRow): RowChoice | null {
  if (row.rosterMatch === null) return null;
  return CHOICE_BY_RESOLUTION[row.rosterResolution] ?? null;
}

export function isImportableRow(row: ImportBatchRow): boolean {
  if (isDroppedRow(row)) return false;
  if (isBlockingStatus(reviewRowStatus(row))) return false;
  return IMPORTABLE_RESOLUTIONS.includes(row.rosterResolution);
}

const LEFT_UNTOUCHED_RESOLUTIONS: readonly RosterResolutionAction[] = ['skip', 'keep_existing'];

/** Live rows the commit leaves out because the creator is already in the
 *  campaign — mirrors the backend's outreach hand-off, which never sends them
 *  on, so the outreach batch's own counts don't include them. */
export function countAlreadyInCampaign(rows: ImportBatchRow[]): number {
  return rows.filter(
    (row) => !isDroppedRow(row) && !hasFieldErrors(row) && LEFT_UNTOUCHED_RESOLUTIONS.includes(row.rosterResolution),
  ).length;
}

export function countImportable(rows: ImportBatchRow[]): number {
  return rows.filter(isImportableRow).length;
}
